import { AVATAR_DIMENSION, AVATAR_MAX_BYTES, AVATAR_MIME } from 'shared';

/**
 * Avatars are downscaled on the player's own device before upload — the server
 * ceiling is a guard, not the resize. A square centre crop at 256px keeps every
 * face the same shape on the projector.
 */
export async function downscaleAvatar(file: Blob): Promise<Blob> {
  const bitmap = await loadBitmap(file);
  const size = AVATAR_DIMENSION;

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas is unavailable');

  // Centre crop to a square, so portrait and landscape photos both fill.
  const side = Math.min(bitmap.width, bitmap.height);
  const sx = (bitmap.width - side) / 2;
  const sy = (bitmap.height - side) / 2;
  ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, size, size);
  if ('close' in bitmap) bitmap.close();

  const blob = await toBlob(canvas, AVATAR_MIME, 0.82);
  if (blob.size <= AVATAR_MAX_BYTES) return blob;

  // One retry at lower quality before giving up, which is enough for any
  // 256px photo in practice.
  const smaller = await toBlob(canvas, AVATAR_MIME, 0.6);
  if (smaller.size > AVATAR_MAX_BYTES) throw new Error('image is too large');
  return smaller;
}

/**
 * `imageOrientation: 'from-image'` applies the EXIF rotation, without which
 * phone photos arrive sideways.
 */
async function loadBitmap(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      /* fall through to the element path */
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('image could not be decoded'));
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('encoding failed'))),
      type,
      quality,
    );
  });
}
